import React, { Fragment, useEffect, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Divider from "@material-ui/core/Divider";
import { makeStyles } from "@material-ui/core/styles";
import { Loader } from "../components/Loader";
import { dateHelperService } from "../services/date-helper.service";
import { compareRevisionState } from "../services/form-version.store";
import { CHANGE_TYPE, toPlainText } from "../utility/FormDiffUtils";

const CHANGE_COLORS = {
  [CHANGE_TYPE.ADDED]: "#e6f4ea",
  [CHANGE_TYPE.REMOVED]: "#fce8e6",
  [CHANGE_TYPE.MODIFIED]: "#fef7e0",
  [CHANGE_TYPE.UNCHANGED]: "transparent",
};

const CHANGE_BORDERS = {
  [CHANGE_TYPE.ADDED]: "#34a853",
  [CHANGE_TYPE.REMOVED]: "#ea4335",
  [CHANGE_TYPE.MODIFIED]: "#f9ab00",
  [CHANGE_TYPE.UNCHANGED]: "#e0e0e0",
};

const CHANGE_TEXT = {
  [CHANGE_TYPE.ADDED]: "Added",
  [CHANGE_TYPE.REMOVED]: "Removed",
  [CHANGE_TYPE.MODIFIED]: "Modified",
  [CHANGE_TYPE.UNCHANGED]: "Unchanged",
};

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    minHeight: "95%",
    minWidth: "80%",
    padding: "6px",
  },
  titleBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  metaBar: {
    display: "flex",
    flexWrap: "wrap",
    gap: "16px",
    padding: "0 24px 8px",
    color: theme.palette.text.secondary,
    fontSize: "13px",
  },
  summaryBar: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "8px",
    padding: "8px 24px",
  },
  spacer: { flex: 1 },
  columnHeaders: {
    display: "flex",
    gap: "12px",
    padding: "8px 24px 0",
    position: "sticky",
    top: 0,
    backgroundColor: theme.palette.background.paper,
    zIndex: 1,
  },
  columnHeader: {
    flex: 1,
    fontWeight: 600,
    fontSize: "13px",
    color: theme.palette.text.secondary,
  },
  rowWrap: {
    marginBottom: "6px",
  },
  row: {
    display: "flex",
    gap: "12px",
  },
  cell: {
    flex: 1,
    minWidth: 0,
    borderLeft: "4px solid",
    borderRadius: "4px",
    padding: "8px 12px",
  },
  node: {
    borderLeft: "4px solid",
    borderRadius: "4px",
    padding: "8px 12px",
    marginBottom: "6px",
  },
  cellEmpty: {
    flex: 1,
    minWidth: 0,
    border: "1px dashed #e0e0e0",
    borderRadius: "4px",
    padding: "8px 12px",
    color: theme.palette.text.disabled,
    fontStyle: "italic",
    fontSize: "13px",
  },
  cellHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  cellType: {
    textTransform: "uppercase",
    fontSize: "11px",
    letterSpacing: "0.5px",
    color: theme.palette.text.secondary,
  },
  cellLabel: {
    fontWeight: 600,
    fontSize: "14px",
    wordBreak: "break-word",
  },
  childrenWrap: {
    marginLeft: "20px",
    marginTop: "6px",
  },
  fieldChanges: {
    display: "flex",
    gap: "12px",
  },
  fieldChangesInner: {
    flex: 1,
    padding: "2px 12px 6px",
  },
  fieldChange: {
    fontSize: "12px",
    margin: "2px 0",
  },
  fieldName: { fontWeight: 600 },
  prevValue: {
    color: "#c5221f",
    textDecoration: "line-through",
    wordBreak: "break-word",
  },
  currValue: { color: "#188038", wordBreak: "break-word" },
  arrow: { margin: "0 6px", color: theme.palette.text.secondary },
  emptyState: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "40vh",
  },
}));

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "(empty)";
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return "(none)";
    }
    return value
      .map((item) =>
        typeof item === "object" ? item.imageName || JSON.stringify(item) : item,
      )
      .join(", ");
  }
  if (typeof value === "object") {
    return Object.entries(value)
      .map(([key, val]) => `${key}: ${val}`)
      .join(", ");
  }
  return toPlainText(String(value));
};

const nodeHasChange = (node) => {
  if (!node) {
    return false;
  }
  if (node.changeType !== CHANGE_TYPE.UNCHANGED) {
    return true;
  }
  return (node.children || []).some((child) => nodeHasChange(child));
};

// A container modified only by a deeper change is shown neutral, not as Modified.
const effectiveChangeType = (node) =>
  node.changeType === CHANGE_TYPE.MODIFIED && !node.directlyChanged
    ? CHANGE_TYPE.UNCHANGED
    : node.changeType;

function Cell({ node, side, classes }) {
  const present = side === "previous" ? node.inPrevious : node.inCurrent;
  if (!present) {
    return <div className={classes.cellEmpty}>— not present —</div>;
  }

  const label =
    toPlainText(side === "previous" ? node.previousLabel : node.currentLabel) ||
    node.buttonName ||
    node.componentType ||
    "Element";

  const effectiveType = effectiveChangeType(node);
  let highlightType = CHANGE_TYPE.UNCHANGED;
  if (effectiveType === CHANGE_TYPE.MODIFIED) {
    highlightType = CHANGE_TYPE.MODIFIED;
  } else if (side === "previous" && effectiveType === CHANGE_TYPE.REMOVED) {
    highlightType = CHANGE_TYPE.REMOVED;
  } else if (side === "current" && effectiveType === CHANGE_TYPE.ADDED) {
    highlightType = CHANGE_TYPE.ADDED;
  }

  return (
    <div
      className={classes.cell}
      style={{
        borderLeftColor: CHANGE_BORDERS[highlightType],
        backgroundColor: CHANGE_COLORS[highlightType],
      }}
    >
      <div className={classes.cellHeader}>
        <span className={classes.cellType}>
          {node.buttonName || node.componentType}
        </span>
        <span className={classes.cellLabel}>{label}</span>
      </div>
    </div>
  );
}

function DiffRow({ node, classes, changesOnly }) {
  if (changesOnly && !nodeHasChange(node)) {
    return null;
  }

  return (
    <div className={classes.rowWrap}>
      <div className={classes.row}>
        <Cell node={node} side="previous" classes={classes} />
        <Cell node={node} side="current" classes={classes} />
      </div>

      {node.changeType === CHANGE_TYPE.MODIFIED &&
        node.fieldChanges.length > 0 && (
          <div className={classes.fieldChanges}>
            <div className={classes.fieldChangesInner} />
            <div className={classes.fieldChangesInner}>
              {node.fieldChanges.map((change) => (
                <div key={change.field} className={classes.fieldChange}>
                  <span className={classes.fieldName}>{change.label}: </span>
                  <span className={classes.prevValue}>
                    {formatValue(change.previous)}
                  </span>
                  <span className={classes.arrow}>&rarr;</span>
                  <span className={classes.currValue}>
                    {formatValue(change.current)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

      {node.children && node.children.length > 0 && (
        <div className={classes.childrenWrap}>
          {node.children.map((child) => (
            <DiffRow
              key={child.id}
              node={child}
              classes={classes}
              changesOnly={changesOnly}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function DiffNode({ node, classes, changesOnly }) {
  if (changesOnly && !nodeHasChange(node)) {
    return null;
  }

  const displayLabel =
    toPlainText(node.currentLabel || node.previousLabel) ||
    node.buttonName ||
    node.componentType ||
    "Element";

  const effectiveType = effectiveChangeType(node);

  return (
    <div
      className={classes.node}
      style={{
        borderLeftColor: CHANGE_BORDERS[effectiveType],
        backgroundColor: CHANGE_COLORS[effectiveType],
      }}
    >
      <div className={classes.cellHeader}>
        <span className={classes.cellType}>
          {node.buttonName || node.componentType}
        </span>
        <span className={classes.cellLabel}>{displayLabel}</span>
        {effectiveType !== CHANGE_TYPE.UNCHANGED && (
          <Chip
            size="small"
            label={CHANGE_TEXT[effectiveType]}
            style={{
              backgroundColor: CHANGE_BORDERS[effectiveType],
              color: "#fff",
              height: "20px",
            }}
          />
        )}
      </div>

      {node.changeType === CHANGE_TYPE.MODIFIED &&
        node.fieldChanges.map((change) => (
          <div key={change.field} className={classes.fieldChange}>
            <span className={classes.fieldName}>{change.label}: </span>
            <span className={classes.prevValue}>
              {formatValue(change.previous)}
            </span>
            <span className={classes.arrow}>&rarr;</span>
            <span className={classes.currValue}>
              {formatValue(change.current)}
            </span>
          </div>
        ))}

      {node.children && node.children.length > 0 && (
        <div className={classes.childrenWrap}>
          {node.children.map((child) => (
            <DiffNode
              key={child.id}
              node={child}
              classes={classes}
              changesOnly={changesOnly}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function CompareRevisionsDialog(props) {
  const { open, onClose } = props;
  const classes = useStyles();
  const [state, setState] = useState({ loading: true });
  const [changesOnly, setChangesOnly] = useState(false);
  const [sideBySide, setSideBySide] = useState(false);

  useEffect(() => {
    const subscription = compareRevisionState.subscribe((res) => {
      setState(res || {});
    });
    return () => subscription.unsubscribe();
  }, []);

  const { loading, error, diff, current, previous } = state;
  const summary = diff?.summary;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="compare-revisions-dialog-title"
      classes={{ paper: classes.dialogPaper }}
      fullWidth
      maxWidth="lg"
    >
      <div className={classes.titleBar}>
        <DialogTitle id="compare-revisions-dialog-title">
          Compare with previous version
        </DialogTitle>
        <IconButton aria-label="close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>

      {loading ? (
        <Loader position="center" loading={loading} />
      ) : error ? (
        <div className={classes.emptyState}>
          <Typography variant="h6">{error}</Typography>
        </div>
      ) : (
        <Fragment>
          {current && previous && (
            <div className={classes.metaBar}>
              <span>
                Comparing{" "}
                <strong>v{previous.publishedVersion || previous.version}</strong>{" "}
                &rarr;{" "}
                <strong>v{current.publishedVersion || current.version}</strong>
              </span>
              <span>Changed by: <strong>{current.lastUpdatedBy}</strong></span>
              <span>
                On:{" "}
                <strong>
                  {dateHelperService.formattedDateFromUTS(current.lastUpdatedDate)}
                </strong>
              </span>
            </div>
          )}

          <div className={classes.summaryBar}>
            <Chip
              size="small"
              label={`${summary?.added || 0} added`}
              style={{ backgroundColor: CHANGE_BORDERS.added, color: "#fff" }}
            />
            <Chip
              size="small"
              label={`${summary?.removed || 0} removed`}
              style={{ backgroundColor: CHANGE_BORDERS.removed, color: "#fff" }}
            />
            <Chip
              size="small"
              label={`${summary?.modified || 0} modified`}
              style={{ backgroundColor: CHANGE_BORDERS.modified, color: "#fff" }}
            />
            <div className={classes.spacer} />
            <FormControlLabel
              control={
                <Switch
                  checked={sideBySide}
                  onChange={(e) => setSideBySide(e.target.checked)}
                  color="primary"
                />
              }
              label="Side-by-side view"
            />
            {summary?.unchanged > 0 && (
              <FormControlLabel
                control={
                  <Switch
                    checked={changesOnly}
                    onChange={(e) => setChangesOnly(e.target.checked)}
                    color="primary"
                  />
                }
                label="Show changes only"
              />
            )}
          </div>
          <Divider />

          <DialogContent>
            {diff && !diff.hasChanges ? (
              <div className={classes.emptyState}>
                <Typography variant="h6">
                  No differences between these versions.
                </Typography>
              </div>
            ) : (
              <Fragment>
                {sideBySide && (
                  <div className={classes.columnHeaders}>
                    <div className={classes.columnHeader}>
                      Previous (v
                      {previous?.publishedVersion || previous?.version})
                    </div>
                    <div className={classes.columnHeader}>
                      Current (v{current?.publishedVersion || current?.version})
                    </div>
                  </div>
                )}

                {diff?.metaChanges?.length > 0 && (
                  <div className={classes.rowWrap}>
                    <Typography
                      variant="subtitle2"
                      style={{ padding: "8px 0 4px" }}
                    >
                      Form details
                    </Typography>
                    {diff.metaChanges.map((change) => (
                      <div key={change.field} className={classes.fieldChange}>
                        <span className={classes.fieldName}>
                          {change.label}:{" "}
                        </span>
                        <span className={classes.prevValue}>
                          {formatValue(change.previous)}
                        </span>
                        <span className={classes.arrow}>&rarr;</span>
                        <span className={classes.currValue}>
                          {formatValue(change.current)}
                        </span>
                      </div>
                    ))}
                    <Divider style={{ margin: "8px 0" }} />
                  </div>
                )}

                {diff?.children?.map((node) =>
                  sideBySide ? (
                    <DiffRow
                      key={node.id}
                      node={node}
                      classes={classes}
                      changesOnly={changesOnly}
                    />
                  ) : (
                    <DiffNode
                      key={node.id}
                      node={node}
                      classes={classes}
                      changesOnly={changesOnly}
                    />
                  ),
                )}
              </Fragment>
            )}
          </DialogContent>
        </Fragment>
      )}
    </Dialog>
  );
}
